import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { workAuthorService } from "../services/workAuthorService";
import {
  WorkAuthorValuesInit,
  getWorkAuthorValues,
  WorkAuthorValuesType,
  WorkAuthorErrorsType,
  workAuthorDisplayId,
} from "./WorkAuthorCommonStore";
import { WorkAuthor, SortOrder } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getAuthorComposerValues,
  AuthorComposerValuesType,
} from "../../authorComposer/store/AuthorComposerCommonStore";
import { isNil } from "../../../common/utils";
import { GridColumn } from "../../common/types";
import { WorkAuthorPublisherCreateStore } from "../../workAuthorPublisher/store";
import { GridChangeSet } from "../../common/types";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { authorComposerService } from "../../authorComposer/services/authorComposerService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class WorkAuthorCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: WorkAuthorErrorsType = {};
  service = workAuthorService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: WorkAuthor | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = WorkAuthorValuesInit();
  authorError: string | undefined;
  authorService = authorComposerService;
  authorSearchValue: string = "";
  authorOptions: AuthorComposerValuesType[] = [];
  lyricsPercentageError: string | undefined;
  musicPercentageError: string | undefined;
  publishersError: string | undefined;
  publishersVisibleColumns: GridColumn[];
  publishersCreateStore: WorkAuthorPublisherCreateStore;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<WorkAuthorValuesType>[] = [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.publishersVisibleColumns = [
      {
        name: "__publisher",
        title: this.langStore.WorkAuthorPublisherPublisherLabel,
      },
      {
        name: "percentage",
        title: this.langStore.WorkAuthorPublisherPercentageLabel,
      },
      {
        name: "durationInYears",
        title: this.langStore.WorkAuthorPublisherDurationInYearsLabel,
      },
      {
        name: "autoRenewalYears",
        title: this.langStore.WorkAuthorPublisherAutoRenewalYearsLabel,
      },
    ];
    this.publishersCreateStore = new WorkAuthorPublisherCreateStore(
      rootStore,
      "publishers_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<WorkAuthorValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<WorkAuthorValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return workAuthorDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.WorkAuthorSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  viewFormAuthor() {
    const id = this.entityValues.showAuthor?.id;
    if (id) {
      this.rootStore.openOrPushOverlay("authorComposer", id, "view");
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity
      if (params.callerData?.entityName === "WorkAuthorPublisher") {
        this.publishersCreateStore.isWaitingForCallback = false;
        this.publishersCreateStore.callbackReturnPath = null;
        yield this.publishersCreateStore.returnedData(params);
        return;
      }

      if (params.callerData?.fieldName === "author") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showAuthor = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.authorComposerOne({
            id: params.returnData.resultId,
          });
          const authorComposer = result.authorComposerOne;
          this.entityValues.showAuthor = {
            displayId: authorComposer.displayId,
            id: authorComposer.id,
          };
          this.setAuthorOptions([
            getAuthorComposerValues(authorComposer, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupWorkAuthorLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  setAuthorOptions(values: AuthorComposerValuesType[]) {
    this.authorOptions = values;
  }

  *loadAuthorValues(): any {
    let where: any = null;
    if (this.authorSearchValue) {
      const searchFilter = {
        OR: [
          {
            firstName: {
              contains: this.authorSearchValue,
              mode: "insensitive",
            },
          },
          {
            lastName: { contains: this.authorSearchValue, mode: "insensitive" },
          },
          { aka: { contains: this.authorSearchValue, mode: "insensitive" } },
        ],
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.authorComposerListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
    });
    this.setAuthorOptions(
      (result.authorComposerListWithOffset || []).map((x: any) =>
        getAuthorComposerValues(x, this.langStore)
      )
    );
  }

  getAuthorOptions() {
    return this.authorOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setAuthorValue(value: string) {
    this.authorSearchValue = value;
    yield this.loadAuthorValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedAuthor(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewAuthorComposer(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "author"
      );
    } else {
      if (value) {
        this.entityValues.showAuthor = value;
        this.entityValues.__author = value.displayId;

        // Copiar AuthorPublisher del autor como WorkAuthorPublisher iniciales
        yield this.copyAuthorPublishersToWorkAuthor(value.id);
      }
    }
  }

  *copyAuthorPublishersToWorkAuthor(authorId: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.authorComposerOne({ id: authorId });
    const author = result.authorComposerOne;

    if (author?.publishers?.length > 0) {
      this.entityValues.showPublishers = author.publishers.map(
        (ap: any, idx: number) => ({
          id: `new-${idx}`,
          __status__: "added" as const,
          showPublisher: {
            id: ap.publisher.id,
            displayId: ap.publisher.pseudonym || ap.publisher.name,
            name: ap.publisher.name,
          },
          __publisher: ap.publisher.pseudonym || ap.publisher.name,
          percentage: ap.percentage,
          durationInYears: ap.durationInYears,
        })
      );
    }
  }

  getLyricsPercentageValue(): number {
    return this.entityValues.lyricsPercentage;
  }

  setLyricsPercentageValue(value: number) {
    this.entityValues.lyricsPercentage = value;
  }

  getMusicPercentageValue(): number {
    return this.entityValues.musicPercentage;
  }

  setMusicPercentageValue(value: number) {
    this.entityValues.musicPercentage = value;
  }

  publishersLoadRow(row: any) {
    this.publishersCreateStore.loadValuesFromRow(row);
  }

  publishersRestore(id: string) {
    const publishers = this.entityValues.showPublishers.filter(
      (x) => x.id === id
    )[0];
    if (publishers) {
      publishers.__status__ = restoredStatus(publishers.__status__);
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  publishersValidateChanges(_changes: GridChangeSet) {
    const errors = this.publishersCreateStore.validateData(
      this.publishersCreateStore.entityValues
    );

    // Uniqueness validation for subentity
    const currentValues = this.publishersCreateStore.entityValues;
    const existingRows = this.entityValues.showPublishers || [];
    const activeRows = existingRows.filter(
      (row: any) => !row.__status__?.includes("deleted")
    );

    // Uniqueness check: publisher
    const isDuplicate_publisher = activeRows.some(
      (row) =>
        row.id !== currentValues.id &&
        row.showPublisher?.id === currentValues.showPublisher?.id &&
        currentValues.showPublisher?.id
    );
    if (isDuplicate_publisher) {
      errors.publisher = {
        message: this.langStore.duplicateValueError,
        fieldLabel: this.langStore.WorkAuthorPublisherPublisherLabel,
      };
    }

    this.publishersCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  publishersCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.publishersCreateStore.entityValues;
      const publishers = this.entityValues.showPublishers;
      for (
        let index = 0;
        index < this.entityValues.showPublishers.length;
        index++
      ) {
        if (publishers[index].id === values.id) {
          publishers[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.publishersCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.publishersCreateStore.entityValues;
      if (!this.entityValues.showPublishers) {
        this.entityValues.showPublishers = [];
      }
      this.entityValues.showPublishers = [
        ...this.entityValues.showPublishers,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.publishersCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const publishers = this.entityValues.showPublishers;
      for (
        let index = 0;
        index < this.entityValues.showPublishers.length;
        index++
      ) {
        if (publishers[index].id === id) {
          if (isOriginalCrudStatus(publishers[index].__status__)) {
            publishers[index] = {
              ...publishers[index],
              __status__: deleteStatus(publishers[index].__status__),
            };
          } else {
            this.entityValues.showPublishers =
              this.entityValues.showPublishers.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.publishers);
    //   this.setCRUDRowsFromValues(newChanges, this.publishersCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.publishers = getNewCRUDRows(newChanges, this.entityValues.publishers, this.uniqueCounter++);
    // }
  }

  addNewAuthorComposer(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "WorkAuthor",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/authorComposer/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = WorkAuthorValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
    this.authorSearchValue = "";
    this.authorOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: WorkAuthorValuesType): WorkAuthorErrorsType {
    const errors: WorkAuthorErrorsType = {};
    this.clearErrors();
    this.authorError = undefined;
    if (values.showAuthor === null || values.showAuthor.id === "") {
      errors.author = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.WorkAuthorAuthorLabel,
      };
    }

    if (isNil(values.lyricsPercentage)) {
      errors.lyricsPercentage = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.WorkAuthorLyricsPercentageLabel,
      };
    }

    if (isNil(values.musicPercentage)) {
      errors.musicPercentage = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.WorkAuthorMusicPercentageLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "WorkAuthorCreateStore"
    );
  }

  *duplicatesWorkAuthor(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const workAuthorInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (workAuthorInstance) {
      this.entityValues = getWorkAuthorValues(
        workAuthorInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la vista del registro creado.
      this.rootStore.router.replace(`/workAuthor/view/${returnData.resultId}`);
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewWorkAuthor(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewWorkAuthor(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      lyricsPercentage: this.entityValues.lyricsPercentage,
      musicPercentage: this.entityValues.musicPercentage,
      author: this.entityValues.showAuthor?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getWorkAuthorValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = WorkAuthorValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    // Subentidad sin alta standalone: se persiste por el create/update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesWorkAuthor(duplicateFromId);
      }

      yield this.loadAuthorValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
